<template>
    <div
        class="top-navigation-tabs__wrap"
    >
        <div
            ref="navScrollContainer"
            class="top-navigation-tabs__scroll"
        >
            <div class="top-navigation-tabs">
                <draggable
                    v-bind="dragOptions"
                    class="flex h-full divide-x divide-ui-lines"
                    :list="widgetGroupList"
                    group="tabs-group-columns"
                    @end="onTabsOrderChange"
                >
                    <TopNavigationTabItem
                        v-for="(group, index) in widgetGroupList"
                        :key="`group-${group.WidgetGroupID || index}`"
                        :is-active="isActiveTab(group)"
                        :group-data="group"
                        :number-of-widget-group="widgetGroupList.length"
                        :style="tabStyles"
                    />
                </draggable>
                <div
                    v-if="isDesktop"
                    ref="newGroupTab"
                    class="add-new-group-button__block min-w-20 py-3.5 sticky right-[-1px] border-r"
                >
                    <RedesignNewGroupPopover
                        v-if="!editMode"
                        @create-from-template="openTemplatesModal"
                        @create-empty-group="onAddGroup"
                    >
                        <div
                            class="add-new-group-button"
                            data-testid="button-add-group-tab"
                            :style="{
                                fontSize: tabStyles.fontSize
                            }"
                        >
                            <redesign-base-icon
                                name="Plus"
                                :size="iconSize"
                            />
                            <span>
                                {{ $t('dashboard.addGroup') }}
                            </span>
                        </div>
                    </RedesignNewGroupPopover>
                </div>
            </div>
        </div>
        <TemplatesGroupCreateModal
            ref="layoutsGroupCreateModalRef"
            :groups-count="props.widgetGroupList?.length || 0"
            @save="onCreateGroupFromTemplate"
        />
    </div>
</template>

<script setup lang="ts">
import draggable from 'vuedraggable'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { WidgetGroupList, WidgetGroupListCreate } from '@/types/dashboard'
import TopNavigationTabItem from '@/components/RedesignComponents/Tabs/TopNavigationTabItem.vue'
import { isActiveGroup, useGroupNavigationInject } from '@/composables/useGroupNavigationTabs'
import { isDesktop } from '@/composables/useBreakpoints'
import { layoutSwitchInterval, layoutSwitchReport, widgetGroupTitleStyles } from '@/composables/globalData/useLayouts'
import { DEFAULT_FONT_SIZE } from '@/enum/default-layout-config'
import { ModalVisibleState } from '@/composables/useWidgetEditModal'
import { widgetPreviewGroupTitleStyles } from '@/composables/usePreviewLayout'
import { useColumnsStatusManage } from '@/composables/useColumnsStatusManage'
import RedesignNewGroupPopover from '@/components/RedesignComponents/App/RedesignNewGroupPopover.vue'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import TemplatesGroupCreateModal from '@/components/RedesignComponents/Modal/TemplatesGroupCreateModal.vue'

const { onAddNewGroup } = useGroupNavigationInject()
const { isInColumnsManageStatus } = useColumnsStatusManage()

interface IProps {
    activeTab?: string | number
    editMode?: boolean
    previewMode?: boolean
    widgetGroupList?: Array<WidgetGroupList>
}

const props = withDefaults(
    defineProps<IProps>(),
    {
        activeTab: undefined,
        widgetGroupList: () => []
    }
)

const emit = defineEmits<{
    (e: 'add-new-group'): void
    (e: 'add-template-group', pl?: WidgetGroupListCreate): void
    (e: 'reorder-groups', pl: Array<WidgetGroupList>): void
}>()

/* Composable */
const { onSwitchTab } = useGroupNavigationInject()

/* Data */
const navScrollContainer = ref()
const newGroupTab = ref()
const layoutsGroupCreateModalRef = ref<InstanceType<typeof TemplatesGroupCreateModal>>()
let tabAutoSwitchInterval: ReturnType<typeof setInterval> | null = null

/* Computed */
const tabStyles = computed(() => {
    return props.previewMode ? widgetPreviewGroupTitleStyles.value : widgetGroupTitleStyles.value
})
const iconSize = computed(() => {
    return Number(tabStyles.value['--size'] || DEFAULT_FONT_SIZE)
})
const circularTimeoutSec = computed(() => {
    const reportSwitching = layoutSwitchReport.value

    // Do not do interval if in edit or switching not configured or in manage column status
    if (!reportSwitching || props.editMode || ModalVisibleState.value || isInColumnsManageStatus.value) {
        return undefined
    }

    return layoutSwitchInterval.value
})
const dragOptions = computed(() => {
    return {
        animation: 200,
        disabled: props.editMode || !isDesktop.value,
        ghostClass: 'moving-tabs',
        swapThreshold: 0.8,
        drag: true,
    }
})

/* Methods */
function openTemplatesModal () {
    layoutsGroupCreateModalRef.value?.open()
}

function onCreateGroupFromTemplate (data: WidgetGroupListCreate) {
    if (onAddNewGroup) {
        onAddNewGroup(data)
    }
    emit('add-template-group', data)
    layoutsGroupCreateModalRef.value?.close()
}

function onTabsOrderChange ({ oldIndex, newIndex }: { oldIndex: number, newIndex: number }) {
    if (oldIndex === newIndex) {
        return
    }
    const groupList = [ ...props.widgetGroupList ]
    emit('reorder-groups', groupList.map((item, index) => {
        return {
            ...item,
            Order: index,
        }
    }))
}

function onAutoTabSwitch () {
    if (!onSwitchTab) {
        return
    }

    if (props.widgetGroupList.length === 0) {
        return
    }

    const activeGroupIndex = props.widgetGroupList.findIndex(group => isActiveTab(group))
    const nextGroupIndex = activeGroupIndex === -1 ? 0 : (activeGroupIndex + 1) % props.widgetGroupList.length

    onSwitchTab(props.widgetGroupList[nextGroupIndex])
}

function addAutoSwitchInterval () {
    if (tabAutoSwitchInterval !== null) {
        removeAutoSwitchInterval()
    }
    if (circularTimeoutSec.value === undefined) {
        return
    }

    tabAutoSwitchInterval = setInterval(onAutoTabSwitch, circularTimeoutSec.value * 1000)
}

function removeAutoSwitchInterval () {
    if (tabAutoSwitchInterval === null) {
        return
    }

    clearInterval(tabAutoSwitchInterval)
    tabAutoSwitchInterval = null
}

function isActiveTab (group: WidgetGroupList) {
    return !!isActiveGroup(group, props.activeTab)
}

function onAddGroup () {
    if (onAddNewGroup) {
        onAddNewGroup()
    }
    emit('add-new-group')
}

async function scrollToActiveTab () {
    const _right_offset = 44
    const _left_offset = 64
    await nextTick()
    const content = navScrollContainer.value
    if (!content) {
        return
    }
    const activeTab = content.querySelector('.is--active')

    if (!activeTab) return

    await nextTick()
    const activeTabBounding = activeTab.getBoundingClientRect()
    const navScrollBounding = content.getBoundingClientRect()
    const newGroupWidth = newGroupTab.value?.getBoundingClientRect()?.width
    let currentOffset = content.scrollLeft
    const maxR = navScrollBounding.width - newGroupWidth
    if ((activeTabBounding.left - _left_offset) < navScrollBounding.left) {
        // To left
        currentOffset -= (navScrollBounding.left - activeTabBounding.left) + _left_offset
    }
    if (activeTabBounding.right > maxR) {
        // To right
        const _diff = activeTabBounding.right - maxR
        currentOffset += _diff + _right_offset
    }
    // console.log('AAAA', currentOffset)
    content.scrollLeft = Math.max(currentOffset, 0)
}

/* Watch */
watch(
    () => props.activeTab,
    scrollToActiveTab
)
watch(
    circularTimeoutSec,
    addAutoSwitchInterval,
    {
        immediate: true
    }
)

/* Hooks */
onMounted(scrollToActiveTab)
onBeforeUnmount(removeAutoSwitchInterval)

defineExpose({
    scrollToActiveTab
})
</script>

<style lang="scss">
.top-navigation-tabs {
    &__wrap {
        @apply relative w-full max-w-full overflow-hidden box-border;
    }

    &__scroll {
        @apply overflow-x-auto overflow-y-hidden scroll-smooth h-full;
        overscroll-behavior: contain auto;
    }

    @apply flex h-full divide-x divide-ui-lines;

    .add-new-group-button__block {
        @apply flex-shrink-0;
        background-color: var(--secondaryBgColor-color);
        border-color: var(--secondaryBgColor-color);
    }

    .add-new-group-button {
        @apply cursor-pointer h-full px-4 xl:px-6 whitespace-nowrap flex items-center gap-2 text-primary-actions hover:text-primary-actions--focus transition duration-100 text-base leading-none font-semibold;
    }
}
</style>
